import { BookOpen, ChevronRight, Plus, RefreshCw, Save, Sparkles, Star } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import CharacterEditor from "../CharacterEditor";
import SpeechToTextButton from "../SpeechToTextButton";
import ShweebumCat from "../ShweebumCat";
import { toast } from "sonner";

const JourneyMode = ({
  step,
  setStep,
  blueprintData,
  setBlueprintData,
  situations,
  setSituations,
  generatingBlueprint,
  blueprintProgress,
  blueprintOutline,
  setBlueprintOutline,
  loading,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  onGenerateBlueprint,
  onCreateFromBlueprint,
  onBack,
}) => {
  return (
    <>
      {/* Step 1: Basics */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <Label>Book Title *</Label>
            <Input placeholder="e.g., Angie Baby" value={blueprintData.title} onChange={(e) => setBlueprintData({...blueprintData, title: e.target.value})} data-testid="blueprint-title" />
          </div>
          
          <CharacterEditor
            characters={(blueprintData.protagonists || []).map(p => ({
              name: p.name || '',
              role: p.role || 'protagonist',
              description: p.description || '',
              age: p.age || '',
              gender: p.gender || '',
              height: p.height || '',
              build: p.build || '',
              hairColor: p.hairColor || '',
              hairStyle: p.hairStyle || '',
              eyeColor: p.eyeColor || '',
              skinTone: p.skinTone || '',
              distinguishingFeatures: p.distinguishingFeatures || '',
              personality: p.personality || '',
              motivation: p.motivation || '',
              backstory: p.backstory || '',
            }))}
            onChange={(characters) => setBlueprintData({ ...blueprintData, protagonists: characters })}
            maxCharacters={30}
            showTitle={true}
          />
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Antagonist Name (optional)</Label>
              <Input placeholder="Villain or opposing force" value={blueprintData.antagonist_name} onChange={(e) => setBlueprintData({...blueprintData, antagonist_name: e.target.value})} />
            </div>
            <div>
              <Label>Antagonist Description</Label>
              <Input placeholder="Brief description" value={blueprintData.antagonist_description} onChange={(e) => setBlueprintData({...blueprintData, antagonist_description: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Genre *</Label>
              <select className="w-full rounded-md border border-border px-3 py-2 text-sm" value={blueprintData.genre} onChange={(e) => setBlueprintData({...blueprintData, genre: e.target.value})} data-testid="blueprint-genre">
                <option value="">Select...</option>
                <option value="fantasy">Fantasy</option>
                <option value="romance">Romance</option>
                <option value="mystery">Mystery</option>
                <option value="thriller">Thriller</option>
                <option value="scifi">Sci-Fi</option>
                <option value="horror">Horror</option>
                <option value="supernatural horror">Supernatural Horror</option>
                <option value="literary">Literary Fiction</option>
                <option value="historical">Historical</option>
                <option value="urban">Urban Fiction</option>
                <option value="drama">Drama</option>
                <option value="action">Action/Adventure</option>
                <option value="paranormal">Paranormal</option>
                <option value="spiritual warfare">Spiritual Warfare</option>
                <option value="autobiography">Autobiography</option>
                <option value="religious fiction">Religious Fiction</option>
              </select>
            </div>
            <div>
              <Label>Tone/Energy *</Label>
              <select className="w-full rounded-md border border-border px-3 py-2 text-sm" value={blueprintData.tone} onChange={(e) => setBlueprintData({...blueprintData, tone: e.target.value})} data-testid="blueprint-tone">
                <option value="">Select vibe...</option>
                <option value="dark and gritty">Dark & Gritty</option>
                <option value="lighthearted and fun">Lighthearted & Fun</option>
                <option value="terrifying and suspenseful">Terrifying & Suspenseful</option>
                <option value="emotional and painful">Emotional & Painful</option>
                <option value="romantic and passionate">Romantic & Passionate</option>
                <option value="mysterious and eerie">Mysterious & Eerie</option>
                <option value="inspirational and uplifting">Inspirational & Uplifting</option>
                <option value="raw and unflinching">Raw & Unflinching</option>
                <option value="whimsical and magical">Whimsical & Magical</option>
                <option value="atmospheric and disturbing">Atmospheric & Disturbing</option>
                <option value="nostalgic yet eerie">Nostalgic Yet Eerie</option>
              </select>
            </div>
            <div>
              <Label>Chapters</Label>
              <div className="space-y-2">
                <input type="number" min={1} max={100} className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background" value={blueprintData.num_chapters} onChange={(e) => setBlueprintData({...blueprintData, num_chapters: parseInt(e.target.value) || 10})} data-testid="blueprint-num-chapters" />
                {situations.filter(s => s.description.trim()).length >= 2 && (
                  <p className="text-xs text-indigo-600 bg-indigo-50 p-2 rounded">
                    💡 <strong>Suggestion:</strong> With {situations.filter(s => s.description.trim()).length} key moments, 
                    {situations.filter(s => s.description.trim()).length <= 3 ? ' 8-12 chapters' : 
                     situations.filter(s => s.description.trim()).length <= 5 ? ' 12-18 chapters' : 
                     situations.filter(s => s.description.trim()).length <= 8 ? ' 18-25 chapters' : ' 25-35 chapters'} would give each moment room to breathe. But you're the author — choose what feels right!
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <Label>World Rules / Lore <span className="text-muted-foreground font-normal">(optional — supernatural rules, mythology, how the magic works)</span></Label>
            <Textarea
              placeholder="e.g., The demon can only manifest through music and sound. Without sound it is weak. People taken by the entity return as shadow servants..."
              value={blueprintData.world_rules}
              onChange={(e) => setBlueprintData({...blueprintData, world_rules: e.target.value})}
              className="min-h-[70px] text-sm mt-1"
              data-testid="blueprint-world-rules"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onBack}>Back</Button>
            <Button 
              variant="outline"
              className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
              onClick={() => {
                if (!blueprintData.title) {
                  toast.error("Please add a title first to save");
                  return;
                }
                try {
                  const _uid = JSON.parse(localStorage.getItem('shweebum_user') || '{}').id || '';
                  const _draftStorageKey = _uid ? `shweebum_story_drafts_${_uid}` : 'shweebum_story_drafts';
                  const drafts = JSON.parse(localStorage.getItem(_draftStorageKey) || '{}');
                  const draftKey = `journey_${blueprintData.title.toLowerCase().trim()}`;
                  drafts[draftKey] = {
                    mode: "journey",
                    blueprintData,
                    situations,
                    savedAt: new Date().toISOString(),
                    title: blueprintData.title,
                    step: 1
                  };
                  localStorage.setItem(_draftStorageKey, JSON.stringify(drafts));
                  setHasUnsavedChanges(false);
                  toast.success(`Draft saved! "${blueprintData.title}" - Come back anytime to continue.`);
                } catch (err) {
                  toast.error("Failed to save draft");
                }
              }}
              data-testid="save-draft-step1-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              Save Draft
            </Button>
            <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={() => {
              if (!blueprintData.title || !blueprintData.protagonists?.[0]?.name || !blueprintData.genre || !blueprintData.tone) {
                toast.error("Fill in all required fields (*)");
                return;
              }
              setStep(2);
            }} data-testid="blueprint-next-btn">
              Next: Add Key Situations
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Key Situations */}
      {step === 2 && !generatingBlueprint && !blueprintOutline && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            List the key moments of your story in order. Add as many as you need — Shweebum will map them all to {blueprintData.num_chapters} chapters.
          </p>

          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
            {situations.map((sit, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-600">Situation {i + 1}</span>
                  {situations.length > 1 && (
                    <button className="text-xs text-red-400 hover:text-red-600" onClick={() => setSituations(situations.filter((_, idx) => idx !== i))}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Textarea
                    placeholder={i === 0 ? "e.g., Angie meets David at a church picnic. He seems charming and attentive..." : "e.g., The first major incident happens. David's mask slips..."}
                    value={sit.description}
                    onChange={(e) => {
                      const updated = [...situations];
                      updated[i] = { ...updated[i], description: e.target.value };
                      setSituations(updated);
                    }}
                    className="min-h-[60px] text-sm pr-10"
                    data-testid={`situation-${i}`}
                  />
                  <SpeechToTextButton
                    onTranscript={(text, append) => {
                      const updated = [...situations];
                      updated[i] = { 
                        ...updated[i], 
                        description: append && sit.description ? sit.description + ' ' + text : text 
                      };
                      setSituations(updated);
                    }}
                    className="absolute right-2 top-2 h-7 w-7"
                    size="icon"
                  />
                </div>
                <select 
                  className="w-full rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground"
                  value={sit.time_marker}
                  onChange={(e) => {
                    const updated = [...situations];
                    updated[i] = { ...updated[i], time_marker: e.target.value };
                    setSituations(updated);
                  }}
                >
                  <option value="Beginning of the story">Beginning of the story</option>
                  <option value="Minutes later">Minutes later</option>
                  <option value="Same day">Same day</option>
                  <option value="Later that day">Later that day</option>
                  <option value="Later that night">Later that night</option>
                  <option value="The very next morning">The very next morning</option>
                  <option value="A few days later">A few days later</option>
                  <option value="A few weeks later">A few weeks later</option>
                  <option value="A month later">A month later</option>
                  <option value="A few months later">A few months later</option>
                  <option value="6 months later">6 months later</option>
                  <option value="A year later">A year later</option>
                  <option value="2 years later">2 years later</option>
                  <option value="3 years later">3 years later</option>
                  <option value="5 years later">5 years later</option>
                  <option value="Several years later">Several years later</option>
                  <option value="Near the middle">Near the middle</option>
                  <option value="Toward the end">Toward the end</option>
                  <option value="The climax">The climax</option>
                  <option value="The ending">The ending</option>
                </select>
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 bg-card pt-2 border-t border-slate-700 space-y-2">
            <Button
              variant="outline"
              className="w-full border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50"
              onClick={() => setSituations([...situations, { description: "", time_marker: "A few months later" }])}
              data-testid="add-situation-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Another Situation ({situations.length} so far)
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button 
                className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                onClick={onGenerateBlueprint}
                disabled={generatingBlueprint}
                data-testid="generate-blueprint-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Map My Story ({situations.filter(s => s.description.trim()).length} situations → {blueprintData.num_chapters} chapters)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Generating Progress */}
      {generatingBlueprint && (
        <div className="py-8 text-center space-y-4">
          <ShweebumCat size="lg" speaking={true} />
          <p className="text-lg font-literary text-foreground">{blueprintProgress.message}</p>
          <div className="w-full max-w-xs mx-auto bg-background rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
              style={{ width: `${(blueprintProgress.part / blueprintProgress.total) * 100}%` }} />
          </div>
          <p className="text-xs text-muted-foreground">
            {blueprintProgress.part === 1 ? 'Analyzing your story blueprint...' :
             blueprintProgress.part === 2 ? 'Mapping situations to chapters...' :
             'Completing the story arc...'}
          </p>
        </div>
      )}

      {/* Step 3: Review Blueprint Outline */}
      {step === 3 && blueprintOutline && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
            <h3 className="font-literary text-lg text-foreground">{blueprintOutline.title || blueprintData.title}</h3>
            {blueprintOutline.logline && <p className="text-sm text-muted-foreground italic mt-1">{blueprintOutline.logline}</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              {blueprintOutline.themes?.map((t, i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
            </div>
          </div>

          {blueprintOutline.characters?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-foreground mb-1">Characters</h4>
              <div className="space-y-1">
                {blueprintOutline.characters.map((c, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium">{c.name}</span> ({c.role}) — {c.description}
                  </p>
                ))}
              </div>
            </div>
          )}

          {blueprintOutline.chapters?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-foreground mb-2">Chapter Outline ({blueprintOutline.chapters.length} chapters)</h4>
              <div className="space-y-2 max-h-[35vh] overflow-y-auto pr-1">
                {blueprintOutline.chapters.map((ch, i) => (
                  <div key={i} className={`p-2 rounded border text-sm ${ch.key_situation ? 'border-indigo-300 bg-indigo-50' : 'border-border bg-card'}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-muted-foreground mt-0.5">Ch {ch.number}</span>
                      <div>
                        <p className="font-medium text-foreground">{ch.title}</p>
                        <p className="text-xs text-muted-foreground">{ch.summary}</p>
                        {ch.key_situation && (
                          <p className="text-xs text-indigo-600 mt-0.5 flex items-center gap-1">
                            <Star className="w-3 h-3" /> Your key moment
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => { setStep(2); setBlueprintOutline(null); }}>Back</Button>
            <Button
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={onCreateFromBlueprint}
              disabled={loading}
              data-testid="create-from-blueprint-btn"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
              Create This Book
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default JourneyMode;
